import { lazy, Suspense, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ClipboardList, Loader2, Pencil, RefreshCw } from "lucide-react";
import { keepLineBreaks, scopeLines } from "@/lib/scope-lines";
import "@uiw/react-md-editor/markdown-editor.css";

// Lazy-loaded so the Markdown editor + its dependencies stay out of the
// initial job-detail chunk and only load when the summary is opened/edited.
const MDEditor = lazy(() => import("@uiw/react-md-editor"));
const MDMarkdown = lazy(() => import("@uiw/react-md-editor").then((m) => ({ default: m.default.Markdown })));

/**
 * Job Summary — the office's "what needs to happen on site" brief (spec:
 * 2026-07-29 office directive). One office→tech channel, rich text (Markdown:
 * bullet lists, bold, italics, headings). Folds in the old plain "Notes for
 * Tech" card: when no summary is written yet, the card shows the auto
 * vendor-WO brief (jobs.tech_notes) as the current brief and lets the office
 * adopt/format it into the summary.
 *
 * #767 (story-first IA): the summary is "SUMMARY — the ask" and lives as the
 * first row of the NOW panel. `embedded` renders it frameless for that slot;
 * `dim` shrinks it to context once THE STORY exists (its instructions still
 * bind — e.g. call-before-invoicing — so it never disappears). The standalone
 * Card wrapper remains for any caller that wants the old presentation.
 */
export function JobSummaryCard({
    jobId,
    summary,
    techNotesFallback,
    canEdit,
    footer,
    embedded = false,
    dim = false,
    postVisit = false,
}: {
    jobId: string;
    summary: string | null;
    /** Auto vendor-WO brief (jobs.tech_notes) shown until a summary is written. */
    techNotesFallback?: string | null;
    /** Office/admin may edit; techs and read-only viewers see it rendered. */
    canEdit: boolean;
    /** e.g. the vendor-WO "View original scope" toggle, rendered under the body. */
    footer?: ReactNode;
    /** Frameless mode for the NOW panel's SUMMARY row (no Card chrome). */
    embedded?: boolean;
    /** Post-visit: the ask dims to context once the story takes the lead. */
    dim?: boolean;
    /** The visit has happened (isPostVisit). "Add one to tell the tech…" is
     *  advice for a job that hasn't gone out — on a finished job it's noise. */
    postVisit?: boolean;
}) {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(summary ?? techNotesFallback ?? "");

    const colorMode =
        typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light";

    const save = useMutation({
        mutationFn: (value: string | null) => api.patch(`/api/jobs/${jobId}`, { summary: value }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["activity-timeline"] });
            setEditing(false);
            toast.success("Summary saved");
        },
        // Surface what the server actually said. A bare "couldn't save" hid
        // Issue #715 (every summary save 400'd on a stale validation guard) —
        // an error the office can read is an error they can report.
        onError: (e: any) => toast.error(e?.body?.message ?? e?.body?.error ?? "Couldn't save the summary"),
    });

    const hasSummary = !!summary?.trim();
    const fallback = techNotesFallback?.trim() ? techNotesFallback.trim() : null;

    // Nothing to show and the viewer can't add one → render nothing (empty-state
    // doctrine: don't take card height for an action a read-only viewer lacks).
    if (!editing && !hasSummary && !fallback && !canEdit) return null;

    const shrink = dim && !editing;

    const body = (
        <div className={cn("space-y-2", embedded ? "" : "p-4", shrink && "opacity-80")}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                    {embedded ? (
                        <span className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                            Summary — the ask
                        </span>
                    ) : (
                        <>
                            <ClipboardList className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                                Summary
                            </span>
                        </>
                    )}
                    <span className="text-[10px] text-muted-foreground">· what to do on site — the tech sees this</span>
                </div>
                {canEdit && !editing && (
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1 text-xs"
                        onClick={() => {
                            setDraft(summary ?? fallback ?? "");
                            setEditing(true);
                        }}
                    >
                        <Pencil className="h-3 w-3" />
                        {hasSummary ? "Edit" : "Add"}
                    </Button>
                )}
            </div>

            {editing ? (
                <div className="space-y-2" data-color-mode={colorMode}>
                    <Suspense
                        fallback={
                            <div className="flex items-center gap-2 text-xs text-foreground/70">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading editor…
                            </div>
                        }
                    >
                        <MDEditor value={draft} onChange={(v) => setDraft(v ?? "")} height={220} preview="edit" />
                    </Suspense>
                    <p className="text-[11px] text-muted-foreground">
                        Formatting: **bold**, *italic*, and <code>-</code> for bullet lists.
                    </p>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            onClick={() => save.mutate(draft.trim() ? draft.trim() : null)}
                            disabled={save.isPending}
                        >
                            {save.isPending ? "Saving…" : "Save"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={save.isPending}>
                            Cancel
                        </Button>
                    </div>
                </div>
            ) : hasSummary ? (
                <div data-color-mode={colorMode}>
                    <Suspense
                        fallback={
                            <p className={cn("whitespace-pre-wrap", shrink ? "text-xs" : "text-sm")}>{summary}</p>
                        }
                    >
                        <MDMarkdown
                            source={keepLineBreaks(summary!)}
                            style={{ background: "transparent", fontSize: shrink ? "0.78rem" : "0.875rem" }}
                        />
                    </Suspense>
                </div>
            ) : fallback ? (
                <>
                    <p className={cn("leading-snug whitespace-pre-line", shrink ? "text-xs" : "text-sm")}>{fallback}</p>
                    {canEdit && (
                        <p className="text-[11px] text-muted-foreground">
                            Auto brief from the work order — hit Add to format it or write your own.
                        </p>
                    )}
                </>
            ) : postVisit ? null : (
                <p className="text-xs text-foreground/70">
                    No summary yet. Add one to tell the tech exactly what needs to happen on site.
                </p>
            )}

            {footer}
        </div>
    );

    if (embedded) return body;

    return (
        <Card className="border-l-4 border-l-amber-500/80 dark:border-l-amber-400/80">
            <CardContent className="p-0">{body}</CardContent>
        </Card>
    );
}

interface QuoteScopeOfferBody {
    offer: { fromQuote: string; reason: "changed" | "unwritten" } | null;
}

/**
 * "Quote changed — update scope?" The quote's lines moved after this job went
 * out (or the office had already edited its scope), so the job kept its text.
 * Update writes the quote's new lines; Keep mine stops asking about this
 * version of the quote. Neither one pings the tech.
 */
export function QuoteScopeOffer({ jobId }: { jobId: string }) {
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: ["job", jobId, "quote-scope"],
        queryFn: () => api.get<QuoteScopeOfferBody>(`/api/jobs/${jobId}/quote-scope`),
        staleTime: 30_000,
    });
    const answer = useMutation({
        mutationFn: (action: "update" | "keep") =>
            api.post(`/api/jobs/${jobId}/quote-scope`, { action, fromQuote: q.data?.offer?.fromQuote ?? "" }),
        onSuccess: (_d, action) => {
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            toast.success(action === "update" ? "Scope updated from the quote" : "Kept your scope");
        },
        onError: (e: any) => {
            qc.invalidateQueries({ queryKey: ["job", jobId, "quote-scope"] });
            toast.error(e?.body?.message ?? "Couldn't update the scope");
        },
    });
    const offer = q.data?.offer;
    if (!offer) return null;
    return (
        <div
            className="space-y-2 rounded-lg border border-amber-400 bg-card p-3 dark:border-amber-500/70"
            data-testid="quote-scope-offer"
        >
            <div className="flex items-center gap-1.5 text-sm font-semibold text-amber-800 dark:text-amber-300">
                <RefreshCw className="h-4 w-4" />
                {offer.reason === "changed" ? "Quote changed — update scope?" : "Use the scope from the quote?"}
            </div>
            <div className="space-y-1 border-l-2 border-amber-400 pl-2 text-sm leading-snug">
                {scopeLines(offer.fromQuote).map((l, i) => (
                    <p key={i}>{l}</p>
                ))}
            </div>
            <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => answer.mutate("update")} disabled={answer.isPending}>
                    Update scope
                </Button>
                <Button size="sm" variant="outline" onClick={() => answer.mutate("keep")} disabled={answer.isPending}>
                    Keep mine
                </Button>
            </div>
        </div>
    );
}
